import { type JSX, onCleanup, onMount, type ParentComponent } from "solid-js";
import type { useNavigate } from "@tanstack/solid-router";
import { safeRedirectTarget } from "@boveda/shared";
import { router } from "@/router";
import { isTauriRuntime } from "@/lib/platform";
import { finishSocialLogin, socialErrorMessage } from "@/lib/social-login";
import {
  clearTokenPresence,
  LEGACY_TOKEN_ACCOUNT,
  markTokenPresent,
  promoteOauthToken,
} from "@/lib/secure-token";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";

/**
 * Token-LESS payload emitted by the Rust deep-link handler (see `lib.rs`
 * `handle_oauth_callback`). The session token deliberately never appears here —
 * it lives only in the native secure store + the Rust-held active token.
 */
interface OauthCallbackEvent {
  status: string;
  redirect?: string;
  error?: string;
}

/**
 * DeepLinkingProvider completes native OAuth sign-in (Phase 4).
 *
 * Google (and others) block OAuth inside embedded WebViews, so the sign-in runs
 * in the system browser and returns via a verified App/Universal Link
 * (`https://app-<env>/auth/social/callback#…`). The OS re-opens the app; the **Rust**
 * deep-link handler parses that URL, stashes the token in the native secure
 * store (never in JS), primes the Rust-held Bearer, and emits a token-less
 * `oauth-callback` Tauri event. This provider listens for that event and
 * finishes the login (`get-session` → register → navigate), re-keying the token
 * from the legacy store slot to the account's `userId` via `promote_oauth_token`
 * — all without the secret ever entering the WebView JS.
 *
 * No-op on web (the in-page `#token` redirect is handled by the
 * `/auth/social/callback` route instead).
 */
export const DeepLinkingProvider: ParentComponent = (props): JSX.Element => {
  // Este provider vive FUERA del <RouterProvider> (AppProviders lo envuelve),
  // así que useNavigate() aquí no tiene router (su closure hacía
  // `null.navigate` al despachar un deep link y el flujo moría sin navegar).
  // Se navega con el singleton del router, misma firma.
  const navigate: ReturnType<typeof useNavigate> = (opts) => router.navigate(opts);
  const { t } = useI18n();

  onMount(() => {
    if (!isTauriRuntime()) {
      return;
    }
    let unlisten: (() => void) | undefined;
    let disposed = false;

    void (async () => {
      // Dynamic import keeps `@tauri-apps/api` out of the web bundle.
      const { listen } = await import("@tauri-apps/api/event");
      const un = await listen<OauthCallbackEvent>("oauth-callback", (event) => {
        void handleOauthCallback(event.payload);
      });
      if (disposed) {
        un();
      } else {
        unlisten = un;
      }
    })();

    onCleanup(() => {
      disposed = true;
      unlisten?.();
    });
  });

  async function handleOauthCallback(payload: OauthCallbackEvent): Promise<void> {
    if (payload.status !== "ok") {
      showToast(socialErrorMessage(t, payload.error ?? "unknown"), "error");
      void navigate({ to: "/login", replace: true });
      return;
    }

    const redirect = safeRedirectTarget(payload.redirect ?? "/");
    // Rust already stored the token in the legacy slot + primed the active token.
    // Mark the legacy slot present so the API transport primes Rust with it for
    // the follow-up `get-session` (whose Bearer Rust injects — no token in JS).
    markTokenPresent(LEGACY_TOKEN_ACCOUNT);

    await finishSocialLogin({
      navigate,
      redirect,
      onUserResolved: async (userId) => {
        // Re-key the legacy store slot to the account's userId, in Rust. Mark the
        // account present so the registry records `hasToken` without JS holding
        // the secret, then drop the now-stale legacy presence.
        markTokenPresent(userId);
        await promoteOauthToken(userId);
        clearTokenPresence(LEGACY_TOKEN_ACCOUNT);
      },
    });
  }

  return <>{props.children}</>;
};
